/**
 * Version-history state for the sources panel.
 *
 * The second slice out of `SourcesPanel.tsx`'s ~130 lines of inline derived
 * state, after the pure `lib/sources/lockState` rules. Unlike those, this group
 * *calls hooks* (`useVersionList`, `useActivateVersion`), so it has to be a
 * hook — and the two React Query calls stay first and unconditional here, in
 * the same order they had in the component, which is what keeps hook order
 * identical. That is the whole risk surface of this move.
 *
 * Grouped rather than folded into one mega-hook on purpose: `SourcesPanel` has
 * ~28 bindings in that block, and a hook returning all of them would relocate
 * the complexity instead of reducing it. Version history is one coherent
 * concern with one owner.
 *
 * The non-obvious rule this encodes: **latest is not the same as current.**
 * "Latest" is the highest version number, independent of which version is
 * active. Editing is only allowed while viewing the latest, so viewing an older
 * version puts the panel in read-only mode — `isViewingLatest` feeds
 * `deriveLockState`.
 */
import { useActivateVersion, useVersionList } from '@/hooks/lesson/useVersions';
import { notify } from '@/lib/notify';

export interface UseSourceVersionsArgs {
  /** True in `api` data mode. Mock mode uses VersionSelector's own fallback. */
  isApi: boolean;
  lessonId?: string;
  /** Version string passed down by the parent, shown before the list loads. */
  currentVersion?: string;
  onVersionChange?: (version: string) => void;
  /** Closes the selector popover after a switch. */
  onSwitched?: () => void;
}

export function useSourceVersions({
  isApi,
  lessonId,
  currentVersion,
  onVersionChange,
  onSwitched,
}: UseSourceVersionsArgs) {
  // Both hooks are called unconditionally, in this order, exactly as the
  // component did. The `undefined` argument is what disables the query in mock
  // mode - not a conditional call.
  const versionListQuery = useVersionList(
    isApi && lessonId ? lessonId : undefined
  );
  const activateVersion = useActivateVersion(
    isApi && lessonId ? lessonId : undefined
  );

  const apiVersions = versionListQuery.data?.versions ?? [];
  const currentVersionRecord = apiVersions.find(v => v.is_current);
  const apiCurrentVersion = currentVersionRecord?.version;

  // Highest version number, independent of which one is active. Skips the
  // optimistic placeholder `useRegenerateLesson` inserts while Apply is in
  // flight: counting it made the panel read the current version as a PAST one
  // for the length of the request - staging bar swapped for "Switch to v2.0",
  // just-staged rows hidden, no "Applying…" - before snapping back on settle.
  const settledVersions = apiVersions.filter(
    v => !(v as { _generating?: boolean })._generating
  );
  const latestVersion =
    isApi && settledVersions.length > 0
      ? [...settledVersions]
          .map(v => v.version)
          .sort((a, b) => parseFloat(b) - parseFloat(a))[0]
      : undefined;

  const isViewingLatest =
    !isApi ||
    !latestVersion ||
    !apiCurrentVersion ||
    apiCurrentVersion === latestVersion;

  // Prefer the resolved current version, falling back to the parent-passed
  // prop for the initial render before the list resolves.
  const displayVersion = isApi
    ? apiCurrentVersion
      ? `v${apiCurrentVersion}`
      : currentVersion
    : currentVersion;

  // Nothing to switch to (one version, or still loading), or a switch is in
  // flight.
  const versionsAvailable = isApi ? apiVersions.length > 1 : true;
  const versionTriggerDisabled =
    !versionsAvailable || activateVersion.isPending;

  const handleVersionChange = async (version: string) => {
    if (isApi && lessonId) {
      try {
        await activateVersion.mutateAsync(version);
        onVersionChange?.(`v${version}`);
        notify.success(`Switched to v${version}`);
      } catch (err: any) {
        notify.error(err, "Couldn't switch version");
      }
    } else {
      onVersionChange?.(version);
    }
    onSwitched?.();
  };

  return {
    apiVersions,
    /** The version row the backend marks current. The panel reads its
     *  `source_ids` to default the staged-source checkboxes and to filter the
     *  visible list when viewing a past version. */
    currentVersionRecord,
    apiCurrentVersion,
    latestVersion,
    isViewingLatest,
    displayVersion,
    versionsAvailable,
    versionTriggerDisabled,
    isSwitching: activateVersion.isPending,
    handleVersionChange,
  };
}
